import React, { useMemo, useState } from 'react';
import { shape, string, number, func, oneOf, bool } from 'prop-types';
import { Paper, IconButton, Tooltip, Box } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import '@mui/icons-material/CheckBoxOutlineBlank';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { Rnd } from 'react-rnd';
import { I18n } from 'utils/i18n';
import FieldConfigDialog from './FieldConfigDialog';
import { getColor } from './signerColors';

const FIELD_PADDING = 12; // theme.spacing(1.5)

const fieldComponents = {
  signature: ({ name, color }) => (
    <>
      <DriveFileRenameOutlineIcon sx={{ fontSize: '1.2rem', color: color.text, mr: 1 }} />
      <div>{name || I18n.t('Fields.SignatureField')}</div>
    </>
  ),
  checkbox: ({ color }) => (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        border: `2px solid ${color.text}`,
        borderRadius: '2px',
      }}
    />
  ),
  date: ({ name, color }) => (
    <>
      <CalendarTodayIcon sx={{ fontSize: '1.2rem', color: color.text, mr: 1 }} />
      <div>{name || I18n.t('Fields.DateField')}</div>
    </>
  ),
  text: ({ name, color }) => (
    <>
      <TextFieldsIcon sx={{ fontSize: '1.2rem', color: color.text, mr: 1 }} />
      <div>{name || I18n.t('Fields.TextField')}</div>
    </>
  ),
};

const DocumentField = ({ field, pdfDimensions, onPositionChange, onDelete, onUpdate }) => {
  const color = useMemo(() => getColor(field.signerIndex), [field.signerIndex]);
  const isCheckbox = field.type === 'checkbox';
  const [configOpen, setConfigOpen] = useState(false);
  const FieldComponent = fieldComponents[field.type];

  // If we don't have PDF dimensions yet, don't render (avoid incorrect positioning)
  if (!pdfDimensions) {
    return null;
  }

  const handleConfigSave = updatedField => {
    onUpdate(field.id, updatedField);
  };

  // Get dimensions from react-pdf
  const scale = pdfDimensions.width / pdfDimensions.originalWidth;
  const pageHeight = pdfDimensions.height; // This is the scaled height of the PDF page

  // Convert stored coordinates (original PDF dimensions) to display coordinates (scaled)
  const displayX = field.x * scale;
  const displayY = field.y * scale; // field.y is bottom-origin PDF coordinate
  const displayWidth = (field.width || (field.type === 'checkbox' ? 44 : 174)) * scale;
  const displayHeight = (field.height || (field.type === 'checkbox' ? 44 : 64)) * scale;

  // Convert PDF Y coordinate (bottom-origin) to browser Y coordinate (top-origin)
  // browserY represents the top-left corner of the field in browser coordinates
  const browserY = pageHeight - displayY - displayHeight;

  return (
    <>
      <Rnd
        position={{
          x: displayX,
          y: browserY,
        }}
        size={{
          width: displayWidth,
          height: displayHeight,
        }}
        onDragStop={(e, d) => {
          // d.x, d.y are top-left in browser coordinates
          // Convert browser Y back to PDF Y (bottom-origin) and scale back to original dimensions
          const originalX = d.x / scale;
          const originalY = (pageHeight - d.y - d.node.offsetHeight) / scale; // d.node.offsetHeight is displayHeight
          onPositionChange(field.id, {
            x: originalX,
            y: originalY,
          });
        }}
        onResizeStop={(e, direction, ref, delta, position) => {
          const offsetMax = Math.max(ref.offsetWidth, ref.offsetHeight);
          const newDisplayHeight = field.type === 'checkbox' ? offsetMax : ref.offsetHeight;
          const newDisplayWidth = field.type === 'checkbox' ? offsetMax : ref.offsetWidth;

          // Convert back to original PDF dimensions
          const originalX = position.x / scale;
          const originalY = (pageHeight - position.y - newDisplayHeight) / scale;
          const originalWidth = newDisplayWidth / scale;
          const originalHeight = newDisplayHeight / scale;

          onUpdate(field.id, {
            width: originalWidth,
            height: originalHeight,
            x: originalX,
            y: originalY,
          });
        }}
        onResize={(e, direction, ref) => {
          if (field.type === 'checkbox') {
            const size = Math.max(ref.offsetWidth, ref.offsetHeight);
            ref.style.width = `${size}px`;
            ref.style.height = `${size}px`;
          }
        }}
        minWidth={field.type === 'checkbox' ? 44 : 124}
        minHeight={field.type === 'checkbox' ? 44 : 21}
        maxWidth={field.type === 'checkbox' ? 64 : 524}
        maxHeight={field.type === 'checkbox' ? 64 : 224}
        bounds="parent"
        enableResizing={{
          bottom: false,
          bottomLeft: false,
          bottomRight: true,
          left: false,
          right: false,
          top: false,
          topLeft: false,
          topRight: false,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            '&:hover .document-field-actions': {
              opacity: 1,
            },
          }}
        >
          <Paper
            elevation={2}
            sx={{
              width: '100%',
              height: '100%',
              padding: isCheckbox ? '2px' : 0,
              borderRadius: 1,
              userSelect: 'none',
              boxShadow: 3,
              backgroundColor: color.bg,
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'move',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isCheckbox ? 'center' : 'flex-start',
                fontSize: '0.9rem',
                fontWeight: 500,
                color: color.text,
                width: '100%',
                height: '100%',
                padding: `${FIELD_PADDING}px`,
                position: 'relative',
                ...(field.mandatory && {
                  '&:after': {
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    content: '"*"',
                    color: '#f44336',
                    marginLeft: '-8px',
                  },
                }),
              }}
            >
              <FieldComponent {...field} color={color} />
            </Box>
          </Paper>
          <Box
            className="document-field-actions"
            sx={theme => ({
              position: 'absolute',
              top: '-30px',
              right: 0,
              display: 'flex',
              gap: 0.5,
              backgroundColor: theme.palette.background.paper,
              p: 0.5,
              borderRadius: '4px',
              boxShadow: theme.shadows[3],
              opacity: 0,
              transition: 'opacity 0.2s',
              '&:hover': {
                opacity: 1,
              },
            })}
          >
            <Tooltip title={I18n.t('Fields.ConfigDialog.configure')}>
              <IconButton size="small" onClick={() => setConfigOpen(true)}>
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={I18n.t('Common.delete')}>
              <IconButton size="small" onClick={() => onDelete(field.id)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Rnd>
      <FieldConfigDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        field={field}
        onSave={handleConfigSave}
      />
    </>
  );
};

DocumentField.propTypes = {
  field: shape({
    id: string.isRequired,
    type: oneOf(['signature', 'checkbox', 'date', 'text']).isRequired,
    x: number.isRequired,
    y: number.isRequired,
    width: number,
    height: number,
    name: string,
    mandatory: bool,
    editable: bool,
    isYesNo: bool,
    signerIndex: number.isRequired,
  }).isRequired,
  containerRef: shape({ current: shape({}) }),
  pdfDimensions: shape({
    originalWidth: number,
    originalHeight: number,
    width: number, // Scaled width
    height: number, // Scaled height
  }),
  onPositionChange: func.isRequired,
  onDelete: func.isRequired,
  onUpdate: func.isRequired,
};

export default DocumentField;
